import { Layout } from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { PHONE_NUMBER, PHONE_HREF } from "@/components/EmergencyBanner";
import { Link } from "react-router-dom";

const LAST_UPDATED = "April 22, 2026";

const PrivacyPolicy = () => (
  <Layout>
    <SeoHead
      title="Privacy Policy | Semper Fi Pump Service"
      description="How Semper Fi Well & Pump Services collects, uses, and protects information submitted through our website and contact forms."
    />
    <section className="container py-16 md:py-24 max-w-3xl mx-auto">
      <h1 className="text-4xl md:text-5xl font-black font-display mb-4">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-10">Last updated: {LAST_UPDATED}</p>

      <div className="prose prose-slate max-w-none space-y-8 text-foreground">
        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Introduction</h2>
          <p className="text-muted-foreground leading-relaxed">
            Semper Fi Well & Pump Services ("we," "us," or "our") respects your privacy. This Privacy Policy
            explains what information we collect when you visit semperfipumps.com, how we use it, and the
            choices you have. By using this website, you agree to the practices described below.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Information We Collect</h2>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed">
            <li>
              <strong className="text-foreground">Information you provide:</strong> name, phone number, email
              address, service address, and message details when you submit a contact or quote form.
            </li>
            <li>
              <strong className="text-foreground">Automatically collected:</strong> device, browser, pages
              viewed, referring URL, and approximate location through cookies and analytics tools such as
              Google Analytics and Google Tag Manager.
            </li>
            <li>
              <strong className="text-foreground">Call tracking:</strong> if you call a number on this site,
              CallRail may record call metadata (caller number, time, duration) to help us measure
              advertising performance.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">How We Use Information</h2>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed">
            <li>To respond to service requests, schedule work, and provide quotes.</li>
            <li>To follow up on inquiries by phone, text, or email.</li>
            <li>To improve our website, services, and marketing performance.</li>
            <li>To comply with legal obligations and protect against fraud or misuse.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Sharing of Information</h2>
          <p className="text-muted-foreground leading-relaxed">
            We do not sell your personal information. We share information only with service providers who
            help us operate the website and our business (for example, hosting, analytics, and call
            tracking), or when required by law.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Cookies & Tracking</h2>
          <p className="text-muted-foreground leading-relaxed">
            We use cookies and similar technologies for analytics and conversion measurement. You can
            disable cookies in your browser settings, but some site features may not work as intended.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Data Retention & Security</h2>
          <p className="text-muted-foreground leading-relaxed">
            We keep submitted information only as long as needed to serve you and meet legal or business
            requirements. We use reasonable safeguards to protect it, but no method of transmission over the
            internet is 100% secure.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Your Choices</h2>
          <p className="text-muted-foreground leading-relaxed">
            You may request access to, correction of, or deletion of your personal information by contacting
            us using the details below. To stop receiving communications from us, reply STOP to a text or
            ask us by phone.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Children's Privacy</h2>
          <p className="text-muted-foreground leading-relaxed">
            This website is not directed to children under 13, and we do not knowingly collect personal
            information from them.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Changes to This Policy</h2>
          <p className="text-muted-foreground leading-relaxed">
            We may update this Privacy Policy from time to time. The "Last updated" date above will reflect
            any changes.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold font-display mb-3">Contact Us</h2>
          <p className="text-muted-foreground leading-relaxed">
            Questions about this policy? Call <a href={PHONE_HREF} className="text-accent font-semibold hover:underline">{PHONE_NUMBER}</a> or
            use our <Link to="/contact" className="text-accent font-semibold hover:underline">contact form</Link>.
            Mailing address: 119 Lacey Ln, Saylorsburg, PA 18353.
          </p>
        </section>
      </div>
    </section>
  </Layout>
);

export default PrivacyPolicy;
